import {
  Component,
  ChangeDetectionStrategy,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { BehaviorSubject, merge, Observable } from 'rxjs';
import {
  filter,
  map,
  scan,
  shareReplay,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';
import { SubSink } from 'subsink';
import { LoggerService, shareReplayRefCount, valuesOf } from '@adreno-common';
import { AnswerType, Inputs } from '../code';
import { isConcreteInput, isMatrixInput } from '../models';
import { QuestionCacheService, WebPopService, WorkService } from '../services';

@Component({
  selector: 'app-script',
  templateUrl: './script.component.html',
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
      }

      .first-row-label {
        padding-top: 2.5rem;
      }

      .second-row-label {
        padding-top: 0.5rem;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScriptComponent implements OnInit, OnDestroy {
  private formGroup$!: Observable<FormGroup>;
  private ready = new BehaviorSubject<boolean>(false);
  private subs = new SubSink();

  isConcreteInput = isConcreteInput;
  isMatrixInput = isMatrixInput;

  viewModel$!: Observable<ViewModel>;

  constructor(
    private workSvc: WorkService,
    private fb: FormBuilder,
    private webPopSvc: WebPopService,
    private sanitizer: DomSanitizer,
    private logger: LoggerService,
    private cacheSvc: QuestionCacheService
  ) {
    this.initFormGroup();
    this.initViewModel();
  }

  private initFormGroup() {
    this.formGroup$ = this.workSvc.scriptInputs$.pipe(
      filter((scriptInputs) => !!scriptInputs?.inputs),
      withLatestFrom(this.workSvc.qNo$),
      map(([scriptInputs, qNo]) => {
        const { inputs, answers } = scriptInputs;
        const qAnswer = this.cacheSvc.cache || answers![qNo!];
        let formGroup: FormGroup;
        if (inputs?.type === 'matrix') {
          const qMatrix = qAnswer as (
            | string
            | number
            | boolean
            | Date
            | undefined
          )[][];
          formGroup = this.fb.group({
            matrix: this.fb.array(
              inputs.rows.map((row, r) =>
                this.fb.array(
                  inputs.columns.map((col, c) =>
                    this.fb.control((qMatrix && qMatrix[r][c]) || undefined, [
                      ...(col.required ? [Validators.required] : []),
                      ...(col.type === 'check' && col.allowOther
                        ? [otherCheckValidatior()]
                        : []),
                      ...(col.type === 'radio' && col.allowOther
                        ? [otherRadioValidatior()]
                        : []),
                    ])
                  )
                )
              )
            ),
          });
        } else {
          formGroup = this.fb.group({
            control: [
              qAnswer || undefined,
              [
                ...(inputs?.required ? [Validators.required] : []),
                ...(inputs?.type === 'check' && inputs?.allowOther
                  ? [otherCheckValidatior()]
                  : []),
                ...(inputs?.type === 'radio' && inputs?.allowOther
                  ? [otherRadioValidatior()]
                  : []),
              ],
            ],
          });
        }
        return formGroup;
      }),
      shareReplayRefCount()
    );

    this.subs.sink = this.formGroup$
      .pipe(switchMap((formGroup) => valuesOf<any>(formGroup)))
      .subscribe((value) => {
        this.cacheSvc.cache = value?.matrix || value?.control || undefined;
      });
  }

  private initViewModel() {
    this.viewModel$ = merge<Partial<ViewModel>>(
      this.workSvc.qNo$.pipe(map((qNo) => ({ qNo }))),
      this.workSvc.scriptInputs$.pipe(
        filter((scriptInputs) => !!scriptInputs?.inputs),
        map((scriptInputs) => {
          return { inputs: scriptInputs.inputs };
        })
      ),
      this.formGroup$.pipe(map((formGroup) => ({ formGroup }))),
      this.workSvc.noNavBack$.pipe(map((noNavBack) => ({ noNavBack }))),
      this.webPopSvc.request$.pipe(
        map((request) => ({
          webRequest: request
            ? this.sanitizer.bypassSecurityTrustResourceUrl(request!)
            : undefined,
        }))
      )
    ).pipe(scan((acc, value) => ({ ...acc, ...value }), {} as ViewModel));
  }

  back() {
    this.cacheSvc.cache = undefined;
    this.workSvc.navBack();
  }

  closeWebCapture() {
    this.webPopSvc.logResponse('agent-closed');
  }

  getControl(fg: FormGroup, row?: number, col?: number) {
    if (row === undefined && col === undefined) {
      return fg.get('control') as FormControl;
    } else {
      return fg.get(`matrix.${row}.${col}`) as FormControl;
    }
  }

  next(fg: FormGroup) {
    const values = fg.value;
    this.workSvc.navNext(values.matrix || values.control);
    this.cacheSvc.cache = undefined;
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    window.removeEventListener('message', this.onMessage);
  }

  ngOnInit(): void {
    window.addEventListener('message', this.onMessage);
  }

  onMessage = (message: MessageEvent<any>) => {
    if (message.origin != 'https://localhost:5000')
      this.logger.debug('ScriptComponent', 'onMessage', {
        origin: message.origin,
        data: message.data,
      });
    if (message.data.type == 'scriptFrame' && message.data.status === 'ready') {
      this.ready.next(true);
    } else if (message.data.type == 'webCapture' && !!message.data.result) {
      this.webPopSvc.logResponse(message.data.result);
    }
  };

  onScriptFrameLoad(scriptFrame: HTMLIFrameElement) {
    this.subs.sink = this.ready
      .pipe(
        filter((ready) => !!ready),
        switchMap(() => this.workSvc.scriptText$),
        filter((scriptText) => !!scriptText?.html && !!scriptText?.context?.tw)
      )
      .subscribe((scriptText) => {
        scriptFrame.contentWindow?.scrollTo(0, 0);
        scriptFrame.contentWindow?.postMessage(scriptText, '*');
      });
  }
}

interface ViewModel {
  formGroup: FormGroup;
  inputs?: Inputs;
  noNavBack?: boolean;
  qNo?: number;
  webRequest?: SafeResourceUrl;
}

function otherCheckValidatior(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const values = (control.value || []) as string[];
    const otherChk = values.find((chk) => chk.startsWith('Other-'));
    return !!otherChk && otherChk == 'Other-'
      ? { otherCheckValidatior: true }
      : null;
  };
}

function otherRadioValidatior(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value as string | null;
    return value?.startsWith('Other-') && value == 'Other-'
      ? { otherRadioValidatior: true }
      : null;
  };
}
